<script setup>
// Persistent notice that the viewer's saved schedule is self-conflicting
// (Figma 4753:107414). The arrival modal only fires once per page-load and is
// gated behind the Save a Seat prompt; this is the way back to it at any time,
// which is why it is deliberately NOT dismissable — the conflict is a state the
// attendee is still in, not a notice they have read.
//
// Sits in the bottom-banner stack above the announcement bar. It carries no
// positioning of its own; the stack owns that (see bottom-banners.vue).
const { loggedIn } = useUserSession();
const { $bus } = useNuxtApp();
const conflicts = useScheduleConflicts();

const count = ref(0);

// Client-only: the conflicts endpoint reads the session, and rendering this
// during SSR would either leak a per-user notice into a cached page or produce a
// hydration mismatch.
const ready = ref(false);
const show = computed(() => ready.value && loggedIn.value && count.value > 0);

const refresh = async () => {
    if (!loggedIn.value) {
        count.value = 0;
        return;
    }
    const groups = await conflicts.fetchConflicts();
    count.value = groups.length;
};

const learnMore = async () => {
    // `force` bypasses both the once-per-visit guard and the Save a Seat gate —
    // this is an explicit request, not an arrival prompt.
    await conflicts.maybeShow(async () => {
        await navigateTo({
            path: '/agenda',
            query: { tab: 'myschedule', view: 'calendar', conflicts: 1 },
        });
    }, { force: true });
    // Resolving from the modal's own flow can clear the last conflict.
    await refresh();
};

onMounted(() => {
    ready.value = true;
    refresh();
});

// Disappear as soon as the last conflict is resolved, and appear for someone who
// logs in without a reload.
watch(loggedIn, refresh);
$bus.on('schedule:changed', () => { refresh(); });
$bus.on('auth:login', () => { refresh(); });
</script>

<template>
    <Transition name="conflict-banner">
        <div
            v-if="show"
            class="block-schedule-conflict-banner pointer-events-auto flex items-center gap-[10px]
                rounded-[10px] bg-[#980000] px-[16px] py-[12px]"
            role="status"
        >
            <p class="m-0 font-hubspot-sans font-light text-[14px] leading-[1.5] tracking-[0.28px] text-white [word-break:break-word]">
                Action is needed on My Schedule.
                <button
                    type="button"
                    data-test="schedule-conflict-banner-learn-more"
                    class="cursor-pointer border-none bg-transparent p-0 font-[inherit] text-[inherit] leading-[inherit] tracking-[inherit] text-white
                        align-baseline
                        underline decoration-dotted decoration-[8.5%] underline-offset-[3px]
                        transition-[text-decoration] duration-200 hover:no-underline"
                    @click="learnMore"
                >Learn More</button>
            </p>
        </div>
    </Transition>
</template>

<style>
.conflict-banner-enter-active,
.conflict-banner-leave-active {
    transition: transform 0.4s ease, opacity 0.4s ease;
}

.conflict-banner-enter-from,
.conflict-banner-leave-to {
    opacity: 0;
    transform: translateX(-100%);
}

/* Matches the announcement bar: hidden while a modal owns the viewport. */
html.locked .block-schedule-conflict-banner {
    display: none;
}

@media (prefers-reduced-motion: reduce) {
    .conflict-banner-enter-active,
    .conflict-banner-leave-active {
        transition: none;
    }

    .conflict-banner-enter-from,
    .conflict-banner-leave-to {
        opacity: 1;
        transform: none;
    }
}
</style>
